import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  CheckSquare, Plus, Trash2, Circle, CheckCircle2, Clock, 
  AlertTriangle, Bot, Send, Calendar as CalendarIcon, Flag, Mic
} from "lucide-react";
import { format, parseISO, isSameDay } from "date-fns";
import AINotetaker from "@/components/AINotetaker";
import HermesChat from "@/components/HermesChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TasksManager() {
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isBotOpen, setIsBotOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [clashWarning, setClashWarning] = useState(null);
  
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    due_date: "",
    due_time: "",
    priority: "medium"
  });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/comms/tasks`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setTasks(res.data);
    } catch (err) {
      toast.error("Failed to load tasks");
    }
  };

  const checkClashes = async (taskData) => {
    try {
      const res = await axios.post(`${API}/comms/tasks/detect-clashes`, taskData, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const createTask = async (ignoreClashes = false) => {
    if (!newTask.title) {
      toast.error("Title is required");
      return;
    }
    
    // Check for clashes first
    if (!ignoreClashes && newTask.due_date) {
      const clashes = await checkClashes(newTask);
      if (clashes?.has_clashes) {
        setClashWarning(clashes);
        return;
      }
    }
    
    try {
      await axios.post(`${API}/comms/tasks`, newTask, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Task created");
      setIsAddOpen(false);
      setClashWarning(null);
      setNewTask({ title: "", description: "", due_date: "", due_time: "", priority: "medium" });
      fetchTasks();
    } catch (err) {
      toast.error("Failed to create task");
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API}/comms/tasks/${taskId}`, updates, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      fetchTasks();
    } catch (err) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/comms/tasks/${taskId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Task deleted");
      fetchTasks();
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };

  const toggleTaskStatus = (task) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    updateTask(task.id, { status: newStatus });
  };

  const getTasksForDate = (date) => {
    return tasks.filter(t => t.due_date && isSameDay(parseISO(t.due_date), date));
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === "all") return true;
    return task.status === filter;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-500";
      case "medium": return "text-amber-500";
      default: return "text-slate-400";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-600";
      case "medium": return "bg-amber-100 text-amber-600";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800">
            TASKS MANAGER
          </h1>
          <p className="text-sm text-slate-500">AI-powered task management with clash detection</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32 bg-white" data-testid="task-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          
          <Dialog open={isBotOpen} onOpenChange={setIsBotOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="task-bot-btn">
                <Bot className="w-4 h-4" />
                Task Bot
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg p-0 overflow-hidden">
              <HermesChat
                contextHint="[Task Management]"
                title="TASK BOT"
                icon={Bot}
                color="from-violet-600 to-violet-700"
                accentColor="text-violet-400"
                layout="dialog"
                placeholder="Ask about your tasks..."
                sessionPrefix="tasks"
                onClose={() => setIsBotOpen(false)}
              />
            </DialogContent>
          </Dialog>
          
          <Dialog open={isAddOpen} onOpenChange={(open) => {
            setIsAddOpen(open);
            if (!open) setClashWarning(null);
          }}>
            <DialogTrigger asChild>
              <Button className="bg-violet-500 hover:bg-violet-600 rounded-lg" data-testid="add-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed']">Create New Task</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {clashWarning && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 text-sm">Scheduling Conflicts Detected</p>
                        <ul className="text-xs text-amber-700 mt-1 space-y-1">
                          {clashWarning.user_conflicts?.map((c, i) => (
                            <li key={i}>
                              {c.task ? `Task: ${c.task.title}` : c.meeting ? `Meeting: ${c.meeting.title}` : 'Conflict'}
                            </li>
                          ))}
                          {clashWarning.resource_conflicts?.map((c, i) => (
                            <li key={`r-${i}`}>Resource conflict: {c.title}</li>
                          ))}
                        </ul>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setClashWarning(null)}
                            className="text-xs"
                          >
                            Edit Task
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => createTask(true)}
                            className="text-xs bg-amber-500 hover:bg-amber-600"
                          >
                            Create Anyway
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <Label>Task Title</Label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    placeholder="Enter task title"
                    className="mt-1"
                    data-testid="task-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    placeholder="Task details..."
                    className="mt-1"
                    data-testid="task-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      className="mt-1"
                      data-testid="task-duedate-input"
                    />
                  </div>
                  <div>
                    <Label>Due Time (optional)</Label>
                    <Input
                      type="time"
                      value={newTask.due_time}
                      onChange={(e) => setNewTask({ ...newTask, due_time: e.target.value })}
                      className="mt-1"
                      data-testid="task-duetime-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={newTask.priority} 
                    onValueChange={(v) => setNewTask({ ...newTask, priority: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={() => createTask(false)} 
                  className="w-full bg-violet-500 hover:bg-violet-600"
                  data-testid="create-task-btn"
                >
                  Create Task
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Calendar Sidebar */}
        <div className="w-80 border-r border-slate-200 bg-white p-4 flex flex-col">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            className="rounded-lg border-0"
            modifiers={{
              hasTask: (date) => getTasksForDate(date).length > 0
            }}
            modifiersStyles={{
              hasTask: { fontWeight: "bold", color: "#8B5CF6" }
            }}
            data-testid="task-calendar"
          />
          
          <div className="mt-4 pt-4 border-t border-slate-200">
            <h3 className="font-medium text-slate-800 mb-2">
              {format(selectedDate, "MMMM d, yyyy")}
            </h3>
            {getTasksForDate(selectedDate).length > 0 ? (
              <div className="space-y-2">
                {getTasksForDate(selectedDate).map((task) => (
                  <div key={task.id} className="flex items-center gap-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${
                      task.priority === 'high' ? 'bg-red-500' :
                      task.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                    }`} />
                    <span className={task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-700'}>
                      {task.title}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No tasks for this day</p>
            )}
          </div>
          
          {/* Stats */}
          <div className="mt-auto pt-4 border-t border-slate-200">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-app rounded-lg p-2">
                <p className="text-lg font-semibold text-slate-800">
                  {tasks.filter(t => t.status === 'todo').length}
                </p>
                <p className="text-xs text-slate-500">To Do</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2">
                <p className="text-lg font-semibold text-amber-600">
                  {tasks.filter(t => t.status === 'in_progress').length}
                </p>
                <p className="text-xs text-slate-500">In Progress</p>
              </div>
              <div className="bg-green-50 rounded-lg p-2">
                <p className="text-lg font-semibold text-green-600">
                  {tasks.filter(t => t.status === 'done').length}
                </p>
                <p className="text-xs text-slate-500">Done</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <ScrollArea className="flex-1 bg-app">
          <div className="p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800 mb-4">
              TASKS ({filteredTasks.length})
            </h2>
            
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12">
                <CheckSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No tasks found</p>
                <p className="text-slate-400 text-sm">Create your first task!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTasks.map((task) => (
                  <div 
                    key={task.id}
                    className={`bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-4 ${
                      task.status === 'done' ? 'opacity-60' : ''
                    }`}
                    data-testid={`task-item-${task.id}`}
                  >
                    <button
                      onClick={() => toggleTaskStatus(task)}
                      className="mt-0.5"
                      data-testid={`toggle-task-${task.id}`}
                    >
                      {task.status === 'done' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Circle className={`w-5 h-5 ${getPriorityColor(task.priority)}`} />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className={`font-semibold ${
                            task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'
                          }`}>
                            {task.title}
                          </h3>
                          {task.description && (
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-task-${task.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-3 mt-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${getPriorityBadge(task.priority)}`}>
                          <Flag className="w-3 h-3 inline mr-1" />
                          {task.priority}
                        </span>
                        
                        {task.due_date && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <CalendarIcon className="w-3 h-3" />
                            {task.due_date}
                          </span>
                        )}
                        
                        {task.due_time && (
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            {task.due_time}
                          </span>
                        )}
                        
                        <Select 
                          value={task.status} 
                          onValueChange={(v) => updateTask(task.id, { status: v })}
                        >
                          <SelectTrigger className="h-6 text-xs w-28 bg-app">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">To Do</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="done">Done</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* AI Notetaker for tasks */}
                        <AINotetaker
                          sourceType="task"
                          sourceId={task.id}
                          sourceName={task.title}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
